import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { authRequestErrorMessage } from "../auth/authRequest";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import EsimCard from "../components/EsimCard";
import { pageItems } from "../lib/pageItems";
import { formatTripRange } from "../lib/dateLocal";
import { formatMoney } from "../lib/partnerPricing";
import { formatBookingStatus, bookingStatusDotClass } from "../lib/bookingStatus";
import { completeVehicleStripeCheckout } from "../lib/stripeCheckoutComplete";

export default function ExternalBookingComplete() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const sessionId = params.get("session_id");
  const [confirmed, setConfirmed] = useState(null);
  const [confirmedEsim, setConfirmedEsim] = useState(null);
  const [error, setError] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);

  const applyConfirmedBooking = useCallback((data) => {
    setConfirmed(data);
    if (data?.esim) setConfirmedEsim(data.esim);
  }, []);

  const confirmPayment = useCallback(async () => {
    if (!sessionId) {
      setError("Missing payment session");
      return;
    }
    setCompleting(true);
    setError(null);
    try {
      const data = await completeVehicleStripeCheckout(API, sessionId);
      applyConfirmedBooking(data);
      toast.success("Payment confirmed — your partner trip is booked");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "payment_not_completed") {
        setError(detail);
        toast.error("Payment was not completed");
      } else if (e.code === "completion_timeout") {
        const msg = e.message || "Booking confirmation is taking longer than expected. Please retry.";
        setError(msg);
        toast.error(msg);
      } else {
        setError(authRequestErrorMessage(e));
        toast.error(authRequestErrorMessage(e));
      }
    } finally {
      setCompleting(false);
    }
  }, [sessionId, applyConfirmedBooking]);

  useEffect(() => {
    if (authLoading || autoStarted) return;
    if (!sessionId) {
      setError("Missing payment session");
      return;
    }
    setAutoStarted(true);
    confirmPayment();
  }, [sessionId, authLoading, autoStarted, confirmPayment]);

  if (!sessionId && !authLoading) {
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="ext-complete-error">
        <h1 className="zc-headline text-3xl">Payment not confirmed</h1>
        <p className="text-[var(--zc-text-2)] mt-3">Missing payment session</p>
        <button type="button" onClick={() => navigate(`/external/${id}`)} className="mt-8 px-5 h-11 zc-btn-primary text-sm">
          Return to listing
        </button>
      </div>
    );
  }

  if (authLoading || completing || (!error && !confirmed)) {
    return (
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20 text-[var(--zc-text-2)] text-center" data-testid="ext-complete-loading">
        Confirming your payment…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="ext-complete-error">
        <h1 className="zc-headline text-3xl">Could not confirm payment</h1>
        <p className="text-[var(--zc-text-2)] mt-3">{error}</p>
        <div className="flex justify-center gap-3 mt-8">
          <button type="button" onClick={confirmPayment} disabled={completing} className="px-5 h-11 zc-btn-primary text-sm">
            Retry
          </button>
          <Link to={`/external/${id}`} className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm">
            Back to listing
          </Link>
        </div>
      </div>
    );
  }

  const snap = confirmed?.vehicle_snapshot || {};
  const currency = confirmed?.currency || "USD";

  return (
    <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-20" data-testid="ext-confirmed">
      <div className="zc-card p-6 sm:p-10 text-center">
        <div className="w-14 h-14 mx-auto grid place-items-center rounded-full" style={{ background: "rgba(22,163,74,0.12)" }}>
          <CheckCircle2 size={24} className="text-[var(--zc-success)]" />
        </div>
        <div className="eyebrow mt-6">Confirmed</div>
        <h1 className="zc-headline text-4xl mt-2">Your partner trip is booked.</h1>
        <p className="text-[var(--zc-text-2)] mt-3 max-w-md mx-auto">
          {snap.make} {snap.model} · {formatTripRange(confirmed.start_date, confirmed.end_date) || "—"}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Booking ID</div>
            <div className="font-mono text-xs">{confirmed.booking_id}</div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Total</div>
            <div className="zc-price text-2xl">{formatMoney(confirmed.total, currency)}</div>
          </div>
          <div className="zc-card p-4">
            <div className="eyebrow mb-1">Status</div>
            <div className="text-sm flex items-center gap-2 mt-1 justify-center">
              <span className={"dot " + bookingStatusDotClass(confirmed.status)} /> {formatBookingStatus(confirmed)}
            </div>
          </div>
        </div>

        <div className="mt-10 zc-card p-5 text-left text-sm text-[var(--zc-text-2)] leading-relaxed" style={{ background: "rgba(22,163,74,0.06)", borderColor: "rgba(22,163,74,0.25)" }}>
          <div className="font-semibold text-[var(--zc-text)] mb-1">What happens next</div>
          Your reservation with {snap.supplier || "our partner"} is confirmed on Z-Cars.
          Pick-up details and any partner-specific instructions will be in your trip summary and email.
          <span className="block mt-2 text-[var(--zc-text-3)]">A confirmation email has been sent to your account email.</span>
        </div>

        <div className="flex justify-center gap-3 mt-8">
          <Link to="/profile/bookings" className="px-5 h-11 grid place-items-center zc-btn-primary text-sm" data-testid="ext-go-trips">View my trips</Link>
          <Link to="/browse" className="px-5 h-11 grid place-items-center zc-btn-ghost text-sm" data-testid="ext-keep-browsing">Keep browsing</Link>
        </div>
      </div>

      {confirmedEsim && (
        <div className="mt-8 text-left" data-testid="ext-esim-block">
          <div className="eyebrow mb-3">Travel Pack · eSIM</div>
          <EsimCard esim={confirmedEsim} onUpdate={async () => {
            try {
              const er = await axios.get(`${API}/esim/mine`, { withCredentials: true });
              const refreshed = pageItems(er.data).find((e) => e.activation_id === confirmedEsim.activation_id);
              if (refreshed) setConfirmedEsim(refreshed);
            } catch (_) {}
          }} />
        </div>
      )}
    </div>
  );
}
